import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api";
import "./EstablishmentPage.css";

interface DJ {
  _id: string;
  name: string;
  avatarUrl?: string;
  slug: string;
  pricePerRequest?: number;
  currency?: string;
  bio?: string;
}

interface EstData {
  _id: string;
  name: string;
  slug: string;
  djs: DJ[];
}

function fmtAvatar(url?: string): string | null {
  if (!url) return null;
  return url.startsWith("http") ? url : `https://api.requesta.app${url}`;
}

export default function EstablishmentPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [est, setEst] = useState<EstData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!slug) return;
    api
      .get(`/establishments/${slug}`)
      .then((r) => setEst(r.data))
      .catch(() => setError("Establishment not found"))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="est-page">
        <div className="est-loader">
          <div className="est-spinner" />
        </div>
      </div>
    );
  }

  if (error || !est) {
    return (
      <div className="est-page">
        <div className="est-error">
          <h2>Not Found</h2>
          <p>{error || "This establishment does not exist."}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="est-page">
      <div className="est-container">
        <header className="est-header">
          <h1 className="est-title">{est.name}</h1>
          <p className="est-subtitle">Select a DJ to make a request</p>
        </header>

        {est.djs.length === 0 ? (
          <p className="est-empty">No DJs available at the moment.</p>
        ) : (
          <div className="est-grid">
            {est.djs.map((dj) => {
              const avatar = fmtAvatar(dj.avatarUrl);
              const price = dj.pricePerRequest ?? 1;
              const cur = (dj.currency || "usd").toUpperCase();
              return (
                <div
                  key={dj._id}
                  className="est-dj-card"
                  onClick={() => navigate(`/dj/${dj.slug}/request`)}
                >
                  <div className="est-dj-avatar-wrap">
                    {avatar ? (
                      <img src={avatar} alt={dj.name} className="est-dj-avatar" />
                    ) : (
                      <div className="est-dj-avatar-placeholder">
                        {dj.name?.[0]?.toUpperCase() || "?"}
                      </div>
                    )}
                  </div>
                  <h3 className="est-dj-name">{dj.name}</h3>
                  {dj.bio && <p className="est-dj-bio">{dj.bio}</p>}
                  <div className="est-dj-price">
                    From {cur === "EUR" ? "€" : "$"}{price}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <footer className="est-footer">
          <span>Powered by </span>
          <strong>Requesta</strong>
        </footer>
      </div>
    </div>
  );
}
